import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, BookOpen } from 'lucide-react';
import { erpAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Modal from '../../components/admin/Modal';
import { rows, errorText, Loading, Form } from '../../utils/erpHelpers';
import './ERPManagement.css';

const ACCOUNT_TYPES = [
  { value: 'ASSET', label: 'Asset', desc: 'Resources owned by the business', color: '#2563eb', bg: '#eef2ff' },
  { value: 'LIABILITY', label: 'Liability', desc: 'Obligations due to others', color: '#dc2626', bg: '#fef2f2' },
  { value: 'EQUITY', label: 'Equity', desc: "Owner's interest in the business", color: '#16a34a', bg: '#f0fdf4' },
  { value: 'REVENUE', label: 'Revenue / Income', desc: 'Earnings from operations', color: '#ca8a04', bg: '#fefce8' },
  { value: 'EXPENSE', label: 'Expense', desc: 'Costs incurred in operations', color: '#ea580c', bg: '#fff7ed' },
];

const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const closeToast = useCallback(() => setMessage(null), []);
  const [form, setForm] = useState<Record<string, any>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('ASSET');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await erpAPI.list('accounts');
      setAccounts(rows(res.data));
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const fn = () => load(); window.addEventListener('admin-refresh', fn);
    return () => window.removeEventListener('admin-refresh', fn);
  }, [load]);

  const value = (key: string) => form[key] ?? '';

  const openCreate = () => {
    setEditingAccount(null);
    setForm({ code: '', name: '', account_type: 'ASSET', is_active: true });
    setModalOpen(true);
  };

  const openEdit = (account: any) => {
    setEditingAccount(account);
    setForm({
      code: account.code, name: account.name,
      account_type: account.account_type, is_active: account.is_active,
    });
    setModalOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        code: value('code'), name: value('name'),
        account_type: value('account_type'), is_active: value('is_active') !== false,
      };
      if (editingAccount?.id) {
        await erpAPI.update('accounts', editingAccount.id, payload);
        setMessage({ type: 'success', text: 'Account updated.' });
      } else {
        await erpAPI.create('accounts', payload);
        setMessage({ type: 'success', text: 'Account created.' });
      }
      setModalOpen(false);
      setForm({});
      setEditingAccount(null);
      await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this account? This action cannot be undone.')) return;
    try {
      await erpAPI.remove('accounts', id);
      setMessage({ type: 'success', text: 'Account deleted.' });
      await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const filteredAccounts = useMemo(() => {
    return accounts
      .filter(x => x.account_type === activeTab)
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [accounts, activeTab]);

  if (loading && !accounts.length) return <Loading />;

  const currentTab = ACCOUNT_TYPES.find(t => t.value === activeTab) || ACCOUNT_TYPES[0];

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={closeToast} duration={message?.type === 'error' ? 5000 : 3500} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOpen size={22} style={{ color: '#6366f1' }} /> Chart of Accounts
          </h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>{currentTab.label} — {currentTab.desc}</p>
        </div>
        <button className="erp-btn" onClick={openCreate} style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
          <Plus size={15} /> Add Account
        </button>
      </div>

      <div style={{ display: 'flex', gap: 9, padding: 4, background: '#f1f5f9', borderRadius: 12, width: 'max-content' }}>
        {ACCOUNT_TYPES.map(t => (
          <button key={t.value} onClick={() => setActiveTab(t.value)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9,
              border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: activeTab === t.value ? '#fff' : 'transparent',
              color: activeTab === t.value ? t.color : '#64748b',
              boxShadow: activeTab === t.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, opacity: activeTab === t.value ? 1 : 0.4 }} />
            {t.label}
            <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 999, background: activeTab === t.value ? `${t.color}12` : '#e2e8f0', color: activeTab === t.value ? t.color : '#94a3b8' }}>
              {accounts.filter(x => x.account_type === t.value).length}
            </span>
          </button>
        ))}
      </div>

      <div className="erp-card" style={{ padding: 0, overflow: 'hidden', borderRadius: 12 }}>
        {filteredAccounts.length === 0 ? (
          <div style={{ padding: '40px 18px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No accounts in this category.</div>
        ) : (
          <div className="erp-table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 100, padding: '9px 14px', fontSize: 11 }}>Code</th>
                  <th style={{ padding: '9px 14px', fontSize: 11 }}>Account Name</th>
                  <th style={{ width: 80, padding: '9px 14px', fontSize: 11 }}>Status</th>
                  <th style={{ width: 70, padding: '9px 14px', fontSize: 11, textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((acc: any) => {
                  const meta = ACCOUNT_TYPES.find(t => t.value === acc.account_type) || ACCOUNT_TYPES[0];
                  return (
                    <tr key={acc.id} style={{ transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '9px 14px' }}>
                        <code style={{
                          fontWeight: 700, fontSize: 12, color: meta.color,
                          background: `${meta.color}08`, padding: '2px 8px', borderRadius: 4,
                        }}>{acc.code}</code>
                      </td>
                      <td style={{ padding: '9px 14px', fontWeight: 600, fontSize: 13 }}>{acc.name}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <span style={{
                          display: 'inline-flex', padding: '3px 10px', borderRadius: 999,
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
                          background: acc.is_active ? '#dcfce7' : '#fee2e2',
                          color: acc.is_active ? '#166534' : '#991b1b',
                        }}>
                          {acc.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                        <div className="erp-actions" style={{ justifyContent: 'center', gap: 4 }}>
                          <button className="erp-icon" onClick={() => openEdit(acc)} title="Edit"
                            style={{ background: '#f1f5f9', padding: 5 }}>
                            <Pencil size={13} />
                          </button>
                          <button className="erp-icon danger" onClick={() => remove(acc.id)} title="Delete"
                            style={{ padding: 5 }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editingAccount ? 'Edit Account' : 'Add Account'} size="medium">
        <Form className="erp-modal-form" onSubmit={save}>
          <p className="erp-modal-intro" style={{ color: '#64748b', fontSize: 13 }}>
            {editingAccount
              ? 'Update the code, name, type, or status of this account.'
              : 'Define a new account with a unique code, descriptive name, and account type.'}
          </p>
          <div className="admin-form-table">
            <div className="admin-form-row">
              <div className="admin-form-label">Account code</div>
              <div className="admin-form-input">
                <input type="text" value={value('code')} placeholder="e.g. 1100"
                  onChange={e => setForm({ ...form, code: e.target.value })} required />
                <div className="admin-form-hint">Must be unique across all accounts. Numeric codes are conventional.</div>
              </div>
            </div>
            <div className="admin-form-row">
              <div className="admin-form-label">Account name</div>
              <div className="admin-form-input">
                <input type="text" value={value('name')} placeholder="e.g. Petty Cash"
                  onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
            </div>
            <div className="admin-form-row">
              <div className="admin-form-label">Account type</div>
              <div className="admin-form-input">
                <SearchableSelect value={value('account_type')}
                  onChange={next => setForm({ ...form, account_type: next })}
                  placeholder="Select type" options={ACCOUNT_TYPES.map(t => ({ value: t.value, label: `${t.label} — ${t.desc}` }))} />
              </div>
            </div>
            <div className="admin-form-row checkbox-row">
              <div className="admin-form-label">Status</div>
              <div className="admin-form-input">
                <label className="admin-toggle-label">
                  <input type="checkbox" checked={value('is_active') !== false}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                  Active account
                </label>
              </div>
            </div>
          </div>
          <div className="admin-form-actions">
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-save">{editingAccount ? 'Update Account' : 'Create Account'}</button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ChartOfAccounts;
